import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtTime12, isLate } from "@/lib/utils";
import { announceAttendance, announceReject, playSoftAlert } from "@/lib/sound";
import { enqueueScan, queueCount, getQueue, removeFromQueue } from "@/lib/kioskQueue";
import { CheckCircle2, XCircle, Camera, CameraOff, Maximize, Minimize, X, ScanLine, Nfc, Usb, HelpCircle, Download, Loader2, WifiOff, UploadCloud, UserPlus, Search } from "lucide-react";

const QR_REGION_ID = "kiosk-qr-reader";
const NFC_BRIDGE_URL = "ws://127.0.0.1:8777";

// Descarga forzando el nombre exacto (evita que el navegador agregue .txt al .bat/.command).
async function descargarArchivo(url, filename) {
  try {
    const res = await fetch(url, { cache: "no-store" });
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 4000);
  } catch {
    window.open(url, "_blank");
  }
}

function BigClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  const h24 = now.getHours();
  const ampm = h24 >= 12 ? "pm" : "am";
  const h12 = h24 % 12 || 12;
  const hh = String(h12).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  const fecha = now.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });
  return (
    <div className="text-right" data-testid="kiosk-clock">
      <div className="font-mono font-bold text-3xl sm:text-5xl tracking-[0.1em] leading-none text-white">
        {hh}:{mm}<span className="text-emerald-300">:{ss}</span> <span className="text-emerald-300 text-xl sm:text-3xl">{ampm}</span>
      </div>
      <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-nardo-300 mt-2 capitalize">{fecha}</div>
    </div>
  );
}

function ResultPhoto({ photoPath }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url; let cancelled = false;
    (async () => {
      if (!photoPath) return;
      try {
        const r = await api.get(`/files/${photoPath}`, { responseType: "blob" });
        url = URL.createObjectURL(r.data);
        if (!cancelled) setSrc(url);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [photoPath]);
  if (!src) {
    return <div className="w-32 h-32 sm:w-44 sm:h-44 rounded-2xl bg-white/10 flex items-center justify-center" />;
  }
  return <img src={src} alt="" className="w-32 h-32 sm:w-44 sm:h-44 rounded-2xl object-cover border-2 border-white/30" data-testid="kiosk-result-photo" />;
}

export default function Kiosk() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lateFor = (k) => (k === "teacher" ? user?.late_after_teacher : user?.late_after);
  const [cameraOn, setCameraOn] = useState(false);
  const [result, setResult] = useState(null); // {ok, data} | {ok:false, message}
  const [isFs, setIsFs] = useState(false);
  const [buffer, setBuffer] = useState("");
  const [bridgeUp, setBridgeUp] = useState(false);      // puente local activo (WebSocket abierto)
  const [readerPresent, setReaderPresent] = useState(false); // ACR122U enchufado
  const [kbActive, setKbActive] = useState(false);      // lector en modo teclado (.exe) detectado
  const [showHelp, setShowHelp] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(queueCount());
  const [syncing, setSyncing] = useState(false);
  // Registrar al toque: enrola una tarjeta no reconocida a un alumno/docente en el momento.
  const [enrollUid, setEnrollUid] = useState(null);     // UID leído a asignar (modal abierto si != null)
  const [people, setPeople] = useState([]);             // alumnos + docentes (cache)
  const [enrollQuery, setEnrollQuery] = useState("");
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [enrollErr, setEnrollErr] = useState("");
  const canEnroll = !!user && user.role !== "docente";
  const inputRef = useRef(null);
  const qrRef = useRef(null);
  const busyRef = useRef(false);
  const resultTimer = useRef(null);
  const flushingRef = useRef(false);
  const kbTimer = useRef(null);
  const lastOkAt = useRef(0); // momento del último registro exitoso (para la gracia de 3s)

  // Marca el lector de teclado (.exe) como activo y lo revierte a "rojo" tras 30s de
  // inactividad. Antes kbActive se ponía true en el primer escaneo y NUNCA volvía a rojo,
  // por eso el estado se quedaba en verde aunque el lector dejara de funcionar.
  const markKbActivity = useCallback(() => {
    setKbActive(true);
    clearTimeout(kbTimer.current);
    kbTimer.current = setTimeout(() => setKbActive(false), 30000);
  }, []);
  useEffect(() => () => clearTimeout(kbTimer.current), []);

  const handleScan = useCallback(async (raw) => {
    const code = (raw || "").trim();
    if (!code || busyRef.current) return;
    busyRef.current = true;
    let dismissMs = 4000;
    const normUid = code.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

    const saveOffline = () => {
      enqueueScan(code);
      setPending(queueCount());
      setResult({ offline: true, ts: new Date().toISOString() });
      playSoftAlert();
    };

    // Sin conexión: no intentamos la red, guardamos de una vez para no perder la lectura.
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      saveOffline();
      setBuffer("");
      clearTimeout(resultTimer.current);
      resultTimer.current = setTimeout(() => { setResult(null); busyRef.current = false; }, 4000);
      return;
    }

    // Si el QR trae un link con /tap/ o /nfc/program/, extrae el token para el respaldo público.
    let token = "";
    for (const marker of ["/tap/", "/nfc/program/"]) {
      const i = code.indexOf(marker);
      if (i !== -1) { token = code.slice(i + marker.length).split(/[/?#]/)[0].trim(); break; }
    }
    try {
      const { data } = await api.post("/attendance/scan", { nfc_uid: code });
      setResult({ ok: true, data });
      lastOkAt.current = Date.now();
      announceAttendance(data.student_name, data.type);
      dismissMs = 3000;
    } catch (e) {
      if (!e.response) {
        // Error de red (servidor sin responder): encolar para sincronizar después.
        saveOffline();
      } else {
        // Respaldo: registro público por token (sirve para cualquier credencial ya impresa).
        const fallback = token || code;
        try {
          const { data } = await api.get(`/attendance/tap/${encodeURIComponent(fallback)}`);
          setResult({ ok: true, data: { ...data, student_name: data.student_name } });
          lastOkAt.current = Date.now();
          announceAttendance(data.student_name, data.type);
          dismissMs = 3000;
        } catch (e2) {
          if (!e2.response) {
            saveOffline();
          } else if (Date.now() - lastOkAt.current < 3000) {
            // Lectura repetida de una credencial recién registrada (el lector la vuelve a leer):
            // dentro de la ventana de 3s NO mostramos la pantalla roja.
            setBuffer("");
            busyRef.current = false;
            return;
          } else {
            setResult({ ok: false, message: e.response?.data?.detail || e2.response?.data?.detail || "Tarjeta no registrada", uid: normUid });
            dismissMs = 8000;
            announceReject();
          }
        }
      }
    } finally {
      setBuffer("");
      clearTimeout(resultTimer.current);
      resultTimer.current = setTimeout(() => { setResult(null); busyRef.current = false; }, dismissMs);
    }
  }, []);

  // --- Registrar al toque (enrolamiento de tarjeta no reconocida) ---
  const openEnroll = useCallback(async (uid) => {
    if (!uid) return;
    clearTimeout(resultTimer.current);   // no dejar que la pantalla roja se cierre sola
    busyRef.current = false;
    setEnrollErr("");
    setEnrollQuery("");
    setEnrollUid(uid);
    if (people.length === 0) {
      try {
        const [st, tc] = await Promise.all([
          api.get("/students?kind=student"),
          api.get("/students?kind=teacher"),
        ]);
        setPeople([...(st.data || []), ...(tc.data || [])]);
      } catch { /* noop */ }
    }
  }, [people.length]);

  const closeEnroll = useCallback(() => {
    setEnrollUid(null);
    setEnrollErr("");
    setResult(null);
    busyRef.current = false;
  }, []);

  const assignCard = useCallback(async (person) => {
    if (!enrollUid || !person) return;
    setEnrollBusy(true);
    setEnrollErr("");
    try {
      await api.put(`/students/${person.id}`, { nfc_uid: enrollUid });
      setPeople((prev) => prev.map((p) => (p.id === person.id ? { ...p, nfc_uid: enrollUid } : p)));
      const uid = enrollUid;
      setEnrollUid(null);
      setResult(null);
      busyRef.current = false;
      await handleScan(uid);   // registra la asistencia de inmediato (ya coincide)
    } catch (e) {
      setEnrollErr(e.response?.data?.detail || "No se pudo registrar la tarjeta. Intenta de nuevo.");
    } finally {
      setEnrollBusy(false);
    }
  }, [enrollUid, handleScan]);
  const flushQueue = useCallback(async () => {
    if (flushingRef.current) return;
    const items = getQueue();
    if (!items.length) { setPending(0); return; }
    flushingRef.current = true;
    setSyncing(true);
    try {
      for (const item of items) {
        try {
          await api.post("/attendance/scan", { nfc_uid: item.code, at: item.at });
          removeFromQueue(item.id);
        } catch (e) {
          if (e.response) {
            // El servidor respondió (p. ej. tarjeta no registrada): no reintentar, descartar.
            removeFromQueue(item.id);
          } else {
            // Error de red: cortamos y reintentamos en el próximo ciclo.
            break;
          }
        }
        setPending(queueCount());
      }
    } finally {
      setPending(queueCount());
      setSyncing(false);
      flushingRef.current = false;
    }
  }, []);

  // Keep the hidden input focused so external USB/Bluetooth NFC readers (keyboard mode) capture the UID
  useEffect(() => {
    // En celulares/tabletas táctiles NO se enfoca (abriría el teclado); ahí se usa NFC/cámara.
    if (!window.matchMedia("(pointer: fine)").matches) return;
    const i = setInterval(() => inputRef.current?.focus(), 900);
    inputRef.current?.focus();
    return () => clearInterval(i);
  }, []);

  // Estado de conexión + sincronización de la cola offline.
  useEffect(() => {
    const goOnline = () => { setOnline(true); flushQueue(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    flushQueue(); // intento inicial por si quedaron lecturas de una sesión previa
    const i = setInterval(() => { if (navigator.onLine && queueCount() > 0) flushQueue(); }, 15000);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(i);
    };
  }, [flushQueue]);

  // Conexión con el "Puente NFC" local (lector ACR122U por PC/SC). El puente publica
  // el UID en ws://127.0.0.1:8777; aquí lo recibimos y registramos la asistencia.
  useEffect(() => {
    let stopped = false;
    let ws = null;
    let retry = null;
    const connect = () => {
      if (stopped) return;
      try {
        ws = new WebSocket(NFC_BRIDGE_URL);
      } catch {
        retry = setTimeout(connect, 3000);
        return;
      }
      ws.onopen = () => { if (!stopped) setBridgeUp(true); };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "uid" && msg.uid) handleScan(msg.uid);
          else if (msg.type === "reader") setReaderPresent(!!msg.connected);
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (stopped) return;
        setBridgeUp(false); setReaderPresent(false);
        retry = setTimeout(connect, 3000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => { stopped = true; clearTimeout(retry); try { ws && ws.close(); } catch { /* noop */ } };
  }, [handleScan]);

  const nfcReady = bridgeUp && readerPresent;

  const startCamera = async () => {
    // Escaneamos el cuadro CASI completo (0.9) para no perder píxeles del QR: el decodificador
    // necesita el QR grande y con detalle. Pedimos MAYOR RESOLUCIÓN (width/height ideal) sin usar
    // constraints exóticos (focusMode) que iOS Safari rechaza y rompían la apertura de la cámara.
    const config = {
      fps: 10,
      qrbox: (vw, vh) => { const m = Math.max(200, Math.floor(Math.min(vw, vh) * 0.9)); return { width: m, height: m }; },
    };
    const onOk = (decodedText) => handleScan(decodedText);
    const onErr = () => { /* ignore per-frame decode errors */ };

    // Cierra y libera cualquier instancia previa (evita el estado "en transición" que rompía
    // los reintentos cuando el primero fallaba a medias).
    const disposeQr = async () => {
      const prev = qrRef.current;
      qrRef.current = null;
      if (prev) { try { await prev.stop(); } catch { /* noop */ } try { await prev.clear(); } catch { /* noop */ } }
    };

    const tryStart = async (camera) => {
      await disposeQr();
      const qr = new Html5Qrcode(QR_REGION_ID, {
        verbose: false,
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      });
      qrRef.current = qr;
      await qr.start(camera, config, onOk, onErr);
    };

    // Intentos de mayor a menor exigencia. Cada uno con instancia NUEVA para no arrastrar fallos.
    const attempts = [
      { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      { facingMode: "environment" },
      { facingMode: "user" },
    ];
    for (const cam of attempts) {
      try { await tryStart(cam); setCameraOn(true); return; } catch { /* siguiente */ }
    }
    // Último recurso: seleccionar cámara por id (laptops/tablets sin "environment").
    try {
      const cams = await Html5Qrcode.getCameras();
      if (cams && cams.length) {
        const back = cams.find((c) => /back|rear|environment|tras/i.test(c.label || "")) || cams[cams.length - 1];
        await tryStart(back.id);
        setCameraOn(true);
        return;
      }
    } catch { /* noop */ }

    await disposeQr();
    setResult({ ok: false, message: "No se pudo abrir la cámara. Revisa los permisos del navegador." });
    clearTimeout(resultTimer.current);
    resultTimer.current = setTimeout(() => setResult(null), 4000);
  };

  const stopCamera = useCallback(async () => {
    const qr = qrRef.current;
    if (qr) {
      try { await qr.stop(); await qr.clear(); } catch { /* noop */ }
      qrRef.current = null;
    }
    setCameraOn(false);
  }, []);

  useEffect(() => () => { stopCamera(); clearTimeout(resultTimer.current); }, [stopCamera]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFs(true);
      } else {
        await document.exitFullscreen();
        setIsFs(false);
      }
    } catch { /* noop */ }
  };
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const exitKiosk = async () => { await stopCamera(); navigate("/scan"); };

  const isIn = result?.ok && result.data.type === "in";
  const late = isIn && isLate(result.data.timestamp, lateFor(result.data.kind));

  return (
    <div className="fixed inset-0 z-[60] bg-navy-ink text-white flex flex-col" data-testid="kiosk-page">
      <div className="h-[3px] w-full bg-navy-light shrink-0" />
      {/* Header */}
      <header className="px-5 sm:px-10 py-4 flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/zavia-icon.png" alt="ZAVIA" data-testid="zavia-icon" className="w-9 h-9 object-contain shrink-0" />
          <div className="leading-none min-w-0">
            <div className="font-display font-extrabold tracking-tight text-base truncate">{user?.school_name || "ZAVIA"}</div>
            <div className="font-display tracking-[0.3em] text-[10px] uppercase text-nardo-300">Control de acceso</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {(!online || pending > 0) && (
            <div
              data-testid="kiosk-conn-status"
              className={`inline-flex items-center gap-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.15em] px-3 py-2 rounded-lg border ${
                !online
                  ? "bg-amber-500/20 border-amber-400/50 text-amber-200"
                  : "bg-sky-500/20 border-sky-400/50 text-sky-200"
              }`}
            >
              {!online ? (
                <><WifiOff className="w-4 h-4" /> Sin conexión{pending > 0 ? ` · ${pending} en cola` : ""}</>
              ) : syncing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sincronizando {pending} pendiente{pending === 1 ? "" : "s"}</>
              ) : (
                <><UploadCloud className="w-4 h-4" /> {pending} pendiente{pending === 1 ? "" : "s"} por enviar</>
              )}
            </div>
          )}
          <BigClock />
        </div>
      </header>

      {/* Main scan area */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-6 min-h-0">
        <div className="text-[11px] sm:text-sm uppercase tracking-[0.4em] text-nardo-300 mb-6 flex items-center gap-3">
          <span className="w-8 h-px bg-white/40" /> Acerca tu pase para registrar <span className="w-8 h-px bg-white/40" />
        </div>

        <div className="relative w-full max-w-md aspect-square rounded-2xl overflow-hidden border-2 border-dashed border-white/25 bg-white/5 flex items-center justify-center">
          <div id={QR_REGION_ID} className="w-full h-full [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />
          {!cameraOn && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6">
              <ScanLine className="w-16 h-16 text-white/40" strokeWidth={1} />
              <p className="text-sm text-nardo-300 max-w-xs">Activa la cámara para leer el código QR del pase, o usa tu lector NFC externo.</p>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {!cameraOn ? (
            <button onClick={startCamera} data-testid="kiosk-start-camera" className="inline-flex items-center gap-2 bg-white text-navy hover:bg-nardo-100 px-7 py-3.5 rounded-xl text-base font-semibold transition-colors">
              <Camera className="w-5 h-5" /> Activar cámara (QR)
            </button>
          ) : (
            <button onClick={stopCamera} data-testid="kiosk-stop-camera" className="inline-flex items-center gap-2 border border-white/30 hover:border-white px-7 py-3.5 rounded-xl text-base font-medium transition-colors">
              <CameraOff className="w-5 h-5" /> Apagar cámara
            </button>
          )}
          <div
            data-testid="kiosk-nfc-status"
            className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] px-4 py-3.5 rounded-xl border transition-colors ${
              nfcReady || kbActive
                ? "bg-emerald-500 border-emerald-400 text-white"
                : bridgeUp
                ? "bg-amber-500/20 border-amber-400/50 text-amber-200"
                : "bg-white/5 border-white/15 text-nardo-300"
            }`}
          >
            {nfcReady ? (
              <><CheckCircle2 className="w-4 h-4" /> Lector NFC conectado</>
            ) : kbActive ? (
              <><CheckCircle2 className="w-4 h-4" /> Lector NFC activo</>
            ) : bridgeUp ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Puente activo · conecta el ACR122U</>
            ) : (
              <><Nfc className="w-4 h-4" /> Acerca tu tarjeta · o abre el lector</>
            )}
          </div>
          {!nfcReady && !kbActive && (
            <button onClick={() => setShowHelp(true)} data-testid="kiosk-nfc-help-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-nardo-300 hover:text-white border border-white/15 hover:border-white/40 px-4 py-3.5 rounded-xl transition-colors">
              <HelpCircle className="w-4 h-4" /> ¿Cómo conecto mi lector?
            </button>
          )}
        </div>
        <p className="mt-4 text-[11px] text-nardo-400 text-center max-w-md">
          <strong>PC Windows con ACR122U:</strong> descarga <strong>UAICARD-LectorNFC-v5.exe</strong> (no necesita Python), ábrelo y acerca la tarjeta: el link se escribe solo. &nbsp;·&nbsp;
          <strong>iPad / tablets:</strong> usa el <strong>QR del pase</strong> (cámara).
        </p>
      </main>

      {/* Bottom controls */}
      <footer className="px-5 sm:px-10 py-4 flex items-center justify-between shrink-0 border-t border-white/10">
        <button onClick={exitKiosk} data-testid="kiosk-exit-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-nardo-300 hover:text-white">
          <X className="w-4 h-4" /> Salir del kiosko
        </button>
        <button onClick={toggleFullscreen} data-testid="kiosk-fullscreen-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-nardo-300 hover:text-white">
          {isFs ? <><Minimize className="w-4 h-4" /> Salir pantalla completa</> : <><Maximize className="w-4 h-4" /> Pantalla completa</>}
        </button>
      </footer>

      {/* Hidden input for external HID NFC reader */}
      <input
        ref={inputRef}
        value={buffer}
        onChange={(e) => { setBuffer(e.target.value.toUpperCase()); if (e.target.value) markKbActivity(); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); markKbActivity(); handleScan(buffer); } }}
        data-testid="kiosk-scan-input"
        inputMode="none"
        tabIndex={-1}
        className="opacity-0 absolute w-px h-px pointer-events-none"
        aria-hidden
      />

      {/* Confirmation overlay — flash de pantalla completa a color, visible desde lejos */}
      {result && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-6" data-testid="kiosk-result">
          <div className={`absolute inset-0 animate-kiosk-flash ${result.offline ? "bg-amber-500" : result.ok ? (!isIn ? "bg-blue-600" : late ? "bg-orange-500" : "bg-emerald-600") : "bg-red-600"}`} />
          {result.offline ? (
            <div className="relative flex flex-col items-center text-center gap-6 max-w-2xl w-full text-white animate-slide-up" data-testid="kiosk-result-offline">
              <UploadCloud className="w-24 h-24 sm:w-28 sm:h-28 text-white drop-shadow" />
              <div className="text-sm uppercase tracking-[0.35em] text-white font-bold">Guardado sin conexión</div>
              <div className="font-display font-black text-4xl sm:text-6xl leading-tight drop-shadow-lg">Lectura registrada</div>
              <div className="font-mono text-2xl sm:text-4xl text-white/90">{fmtTime12(result.ts)}</div>
              <div className="text-base sm:text-xl text-white/90">Se sincronizará automáticamente al volver el internet.</div>
            </div>
          ) : result.ok ? (
            <div className="relative flex flex-col items-center text-center gap-6 max-w-2xl w-full text-white animate-slide-up">
              <div className="relative">
                <span className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
                <ResultPhoto photoPath={result.data.photo_path} />
              </div>
              <CheckCircle2 className="w-20 h-20 sm:w-24 sm:h-24 text-white drop-shadow" />
              <div className="text-sm sm:text-lg uppercase tracking-[0.35em] text-white font-bold" data-testid="kiosk-result-label">
                {result.data.movement || (!isIn ? "Salida registrada" : late ? "Entrada con retardo" : "Entrada registrada")}
              </div>
              <div className="font-display font-black text-5xl sm:text-7xl leading-tight drop-shadow-lg">{result.data.student_name}</div>
              <div className="font-mono text-2xl sm:text-4xl text-white/90">{fmtTime12(result.data.timestamp)}</div>
            </div>
          ) : (
            <div className="relative flex flex-col items-center text-center gap-6 max-w-2xl w-full text-white animate-slide-up">
              <XCircle className="w-24 h-24 sm:w-28 sm:h-28 text-white drop-shadow" />
              <div className="text-sm uppercase tracking-[0.35em] text-white font-bold">No registrado</div>
              <div className="font-display font-black text-4xl sm:text-6xl leading-tight drop-shadow-lg break-all">{result.message}</div>
              {canEnroll && result.uid ? (
                <>
                  <button
                    onClick={() => openEnroll(result.uid)}
                    data-testid="kiosk-enroll-btn"
                    className="inline-flex items-center gap-2 bg-white text-red-700 hover:bg-red-50 px-7 py-4 rounded-xl text-lg font-bold transition-colors shadow-lg"
                  >
                    <UserPlus className="w-6 h-6" /> Registrar esta tarjeta
                  </button>
                  <div className="text-base sm:text-xl text-white/90">Asigna esta tarjeta a un alumno una sola vez y quedará lista para siempre.</div>
                </>
              ) : (
                <div className="text-lg sm:text-2xl text-white/90">Intenta de nuevo o avisa en recepción.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Registrar al toque: asignar una tarjeta no reconocida a un alumno/docente */}
      {enrollUid && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="kiosk-enroll-modal">
          <div className="absolute inset-0 bg-navy-ink/90 backdrop-blur-sm" onClick={closeEnroll} />
          <div className="relative bg-white text-navy rounded-3xl w-full max-w-xl max-h-[88vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-nardo-100">
              <div className="font-display font-bold text-lg inline-flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-600" /> Registrar tarjeta</div>
              <button onClick={closeEnroll} data-testid="kiosk-enroll-close" className="text-nardo-400 hover:text-navy p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 pt-4 pb-2 shrink-0">
              <div className="text-[11px] uppercase tracking-[0.25em] text-nardo-500 mb-1">Número de la tarjeta leída</div>
              <div className="font-mono text-base font-bold text-navy bg-nardo-50 border border-nardo-200 rounded-lg px-3 py-2 break-all" data-testid="kiosk-enroll-uid">{enrollUid}</div>
              <p className="text-sm text-nardo-600 mt-3">Elige a la persona a la que pertenece esta tarjeta. Quedará vinculada y funcionará para siempre.</p>
              <div className="relative mt-3">
                <Search className="w-4 h-4 text-nardo-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  autoFocus
                  value={enrollQuery}
                  onChange={(e) => setEnrollQuery(e.target.value)}
                  data-testid="kiosk-enroll-search"
                  placeholder="Buscar por nombre o grado…"
                  className="w-full border border-nardo-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-navy outline-none focus:border-navy"
                />
              </div>
              {enrollErr && <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="kiosk-enroll-error">{enrollErr}</div>}
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-4">
              {(() => {
                const q = enrollQuery.trim().toLowerCase();
                const list = people
                  .filter((p) => !q || [p.full_name, p.grade, p.level, p.nfc_uid].filter(Boolean).join(" ").toLowerCase().includes(q))
                  .slice(0, 60);
                if (people.length === 0) return <div className="text-center text-nardo-400 text-sm py-8">Cargando personas…</div>;
                if (list.length === 0) return <div className="text-center text-nardo-400 text-sm py-8">Sin resultados para “{enrollQuery}”.</div>;
                return list.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => assignCard(p)}
                    disabled={enrollBusy}
                    data-testid={`kiosk-enroll-person-${p.id}`}
                    className="w-full text-left flex items-center justify-between gap-3 px-3 py-3 rounded-xl hover:bg-nardo-50 disabled:opacity-50 transition-colors border-b border-nardo-50"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-navy truncate">{p.full_name}</div>
                      <div className="text-xs text-nardo-500 truncate">
                        {(p.kind === "teacher" ? "Docente" : (p.grade || "Alumno"))}
                        {p.nfc_uid ? <span className="text-amber-600"> · ya tiene UID {p.nfc_uid} (se reemplazará)</span> : <span className="text-emerald-600"> · sin tarjeta</span>}
                      </div>
                    </div>
                    {enrollBusy ? <Loader2 className="w-5 h-5 animate-spin text-nardo-400 shrink-0" /> : <UserPlus className="w-5 h-5 text-emerald-600 shrink-0" />}
                  </button>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Ayuda: cómo conectar el lector NFC (puente ACR122U + tablets) */}
      {showHelp && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="kiosk-nfc-help">
          <div className="absolute inset-0 bg-navy-ink/85 backdrop-blur-sm" onClick={() => setShowHelp(false)} />
          <div className="relative bg-white text-navy rounded-3xl w-full max-w-2xl max-h-[88vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-nardo-100 sticky top-0 bg-white rounded-t-3xl">
              <div className="font-display font-bold text-lg inline-flex items-center gap-2"><Nfc className="w-5 h-5 text-emerald-600" /> Conectar tu lector NFC</div>
              <button onClick={() => setShowHelp(false)} data-testid="kiosk-nfc-help-close" className="text-nardo-400 hover:text-navy p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-6 text-sm">
              <div>
                <div className="inline-flex items-center gap-2 font-semibold text-navy mb-2"><Usb className="w-4 h-4 text-emerald-600" /> PC Windows con lector ACR122U</div>
                <p className="text-nardo-600 mb-3">Descarga <strong>un solo programa</strong> (no necesita instalar nada). Al abrirlo, lee el <strong>link de programación</strong> que grabaste en la tarjeta (o su número si no tiene link) y lo "escribe" como si fuera un teclado, registrando la asistencia. Funciona aunque el kiosko esté en internet (HTTPS).</p>
                <ol className="list-decimal list-inside text-nardo-600 space-y-1 mb-3">
                  <li>Conecta el ACR122U por USB (enciende su luz roja).</li>
                  <li>Descarga <strong>UAICARD-LectorNFC-v5.exe</strong> y dale <strong>doble clic</strong> (deja esa ventanita abierta). Debe decir <strong>v5-URL</strong> arriba.</li>
                  <li>Si Windows avisa "protegió tu PC": <strong>Más información → Ejecutar de todas formas</strong>.</li>
                  <li>Vuelve al kiosko, haz clic una vez en la pantalla y acerca la tarjeta. ¡El botón se pone verde solo!</li>
                </ol>
                <p className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3" data-testid="nfc-v6-note"><strong>Importante:</strong> el lector lee el <strong>link de programación</strong> grabado en cada tarjeta (así se identifica a cada persona). La ventana negra muestra líneas <strong>[debug]</strong> con lo que lee: déjala abierta. Borra la versión anterior y descarga esta.</p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => descargarArchivo("/nfc-bridge/UAICARD-LectorNFC-v7.exe", "UAICARD-LectorNFC-v7.exe")} data-testid="dl-lector-exe" className="inline-flex items-center gap-2 text-sm font-semibold bg-emerald-600 text-white px-4 py-2.5 rounded-lg hover:bg-emerald-700"><Download className="w-4 h-4" /> Descargar lector para Windows (.exe)</button>
                  <button onClick={() => descargarArchivo("/nfc-bridge/UAICARD-LectorNFC-v7.zip", "UAICARD-LectorNFC-v7.zip")} data-testid="dl-lector-zip" className="inline-flex items-center gap-2 text-sm font-semibold border border-emerald-600 text-emerald-700 px-4 py-2.5 rounded-lg hover:bg-emerald-50"><Download className="w-4 h-4" /> Descargar en .zip</button>
                  <button onClick={() => descargarArchivo("/nfc-bridge/LEEME.txt", "LEEME.txt")} data-testid="dl-bridge-readme" className="inline-flex items-center gap-2 text-sm font-semibold border border-navy/30 text-navy px-4 py-2.5 rounded-lg hover:bg-navy/5"><Download className="w-4 h-4" /> Instrucciones</button>
                </div>
                <p className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3" data-testid="nfc-smartscreen-note"><strong>Si Edge dice "no se descarga habitualmente":</strong> en Descargas pasa el mouse sobre el archivo → <strong>⋯</strong> → <strong>Conservar</strong> → <strong>Mostrar más</strong> → <strong>Conservar de todos modos</strong>. Al abrirlo, si sale "Windows protegió su PC": <strong>Más información</strong> → <strong>Ejecutar de todas formas</strong>. Es seguro: es el lector de tu colegio.</p>

                <details className="mt-3">
                  <summary className="text-[12px] text-nardo-500 cursor-pointer select-none">Otras opciones (Mac o versión con Python)</summary>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button onClick={() => descargarArchivo("/nfc-bridge/UAICARD-PuenteNFC.command", "UAICARD-PuenteNFC.command")} data-testid="dl-bridge-mac" className="inline-flex items-center gap-2 text-xs font-semibold border border-navy/30 text-navy px-3 py-2 rounded-lg hover:bg-navy/5"><Download className="w-4 h-4" /> Mac (.command)</button>
                    <button onClick={() => descargarArchivo("/nfc-bridge/UAICARD-PuenteNFC.bat", "UAICARD-PuenteNFC.bat")} data-testid="dl-bridge-win" className="inline-flex items-center gap-2 text-xs font-semibold border border-navy/30 text-navy px-3 py-2 rounded-lg hover:bg-navy/5"><Download className="w-4 h-4" /> Windows con Python (.bat)</button>
                  </div>
                </details>
                <p className="text-[11px] text-nardo-400 mt-2">El <strong>.exe</strong> es la forma más simple: no requiere Python ni instalación. En Mac usa el archivo <strong>.command</strong>.</p>
              </div>
              <div className="border-t border-nardo-100 pt-5">
                <div className="inline-flex items-center gap-2 font-semibold text-navy mb-2"><Nfc className="w-4 h-4 text-emerald-600" /> iPad / tablets Android</div>
                <p className="text-nardo-600">El ACR122U <strong>no funciona</strong> en iPad ni tablets por límites de Apple/Android. Opciones que sí funcionan en tablet:</p>
                <ul className="list-disc list-inside text-nardo-600 space-y-1 mt-2">
                  <li>Escanear el <strong>QR del pase</strong> del alumno con la cámara (ya funciona aquí).</li>
                  <li>Usar un <strong>lector NFC en “modo teclado”</strong> (Bluetooth o USB). El kiosko ya lo reconoce: solo emparéjalo y acerca la tarjeta — teclea el número solo.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
